import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery } from "react-query";

import ApiAgent from "api/api.agent";
import { selectCapabilitiesLoaded, selectCapability, setCapabilities } from "reducers/agentSlice";

/**
 * What the agent surface will let this reader do: which corpora their subscriptions grant,
 * which tools they may ground a turn on, and whether Ask is among them.
 *
 * Fetched once per session and mirrored into the store, so a gate can read it synchronously
 * from either tree. The page and the rail both call this; react-query dedupes them into one
 * request.
 */
/**
 * `enabled` is for callers that render before there is a reader to ask about. The sidebar is one:
 * it draws on the public pages too, where this call has no session behind it and nothing to gate.
 */
export const useAgentCapabilities = ({ enabled = true } = {}) => {
    const dispatch = useDispatch();

    /**
     * A 403 here is not a failure: it means the reader is not in AGENT-ARBO, so the AI
     * surfaces are simply unavailable and every page falls back to its plain listing.
     */
    const query = useQuery(["agent", "capabilities"], () => ApiAgent.getCapabilities(), {
        enabled,
        staleTime: Infinity,
        retry: false,
        onError: (error) => {
            if (error?.status === 403) {
                dispatch(setCapabilities({ denied: true, detail: error?.data?.detail }));
            }
        },
    });

    useEffect(() => {
        if (query.data) dispatch(setCapabilities(query.data));
    }, [query.data, dispatch]);

    return query;
};

/**
 * Whether this reader may ask, and whether that is known yet. `settled` is true once the server
 * has answered either way, or failed: a failure says nothing about the subscription, so callers
 * that must pick something (the landing page) treat it as "may not ask" rather than wait.
 */
export const useAskAccess = ({ enabled = true } = {}) => {
    const { isError } = useAgentCapabilities({ enabled });
    const loaded = useSelector(selectCapabilitiesLoaded);
    const allowed = useSelector(selectCapability("chat"));
    return { allowed, settled: loaded || isError };
};

export default useAgentCapabilities;
